import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { trackContact } from '../utils/analytics';

const FooterContainer = styled.footer`
  background-color: ${props => props.theme.colors.dark};
  color: white;
  padding: 3rem 0 1rem;
  margin-top: 4rem;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FooterSection = styled.div`
  h3 {
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.secondary};
  }
  
  p, li {
    margin-bottom: 0.5rem;
    color: #cbd5e0;
  }
  
  ul {
    list-style: none;
    padding: 0;
  }
  
  a {
    color: #cbd5e0;
    text-decoration: none;
    
    &:hover {
      color: white;
      text-decoration: underline;
    }
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #4a5568;
  color: #a0aec0;
  font-size: 0.9rem;
`;

const HeartIcon = styled.span`
  color: #e53e3e;
  margin: 0 0.2rem;
`;

const Footer = () => {
  // Smooth scroll to top function with optimized timing
  const scrollToTop = () => {
    // Use requestAnimationFrame for better coordination with browser rendering
    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <h3>About Us</h3>
          <p>
            This website documents the miraculous life-saving events of Maggie Soward's 
            cardiac arrest on Thanksgiving Day 2024, celebrating faith, family resilience, 
            community support,
          </p>
          <p>
            With only a 5% chance of survival, Maggie's story is a testament to the power 
            of quick action, community support, divine intervention, and the mercy and grace of our Savior.
          </p>
          <p>
            Created by the Soward family to honor all who participated in this miracle 
            and to inspire others about the importance of CPR training and community support.
          </p>
        </FooterSection>

        <FooterSection>
          <h3>Contact</h3>
          <p>
            <strong>Scott Soward</strong><br/>
            Website Administrator
          </p>
          <p>
            <a href="mailto:scott.soward@gmail.com" onClick={() => trackContact('email')}>
              📧 scott.soward@gmail.com
            </a>
          </p>
          <p style={{fontSize: '0.9rem', color: '#a0aec0', marginTop: '1rem'}}>
            For questions about this website, sharing your own story, or information 
            about CPR training and heart health resources.
          </p>
        </FooterSection>
        
        <FooterSection>
          <h3>Key Heroes</h3>
          <ul>
            <li><strong>Eliza Soward</strong> - Quick CPR action</li>
            <li><strong>First Responders</strong> - Saratoga Springs Emergency Team</li>
            <li><strong>Heather Jordan</strong> - Child care</li>
            <li><strong>Kirsten Hall</strong> - Child care</li>
            <li><strong>Tiffany Bryant</strong> - Child care</li>
            <li><strong>Bishop Clint Nelson</strong> - Spiritual support</li>
            <li><strong>Medical Team</strong> - Holy Cross Hospital - Mountain Point & St. Mark's Hospital</li>
          </ul>
        </FooterSection>
        
        <FooterSection>
          <h3>Heart Health Resources</h3>
          <ul>
            <li>
              <a href="https://www.heart.org" target="_blank" rel="noopener noreferrer">
                American Heart Association
              </a>
            </li>
            <li>
              <a href="https://cpr.heart.org" target="_blank" rel="noopener noreferrer">
                CPR Training Resources
              </a>
            </li>
            <li>
              <a href="https://www.redcross.org/take-a-class/cpr" target="_blank" rel="noopener noreferrer">
                Red Cross CPR Classes
              </a>
            </li>
            <li>
              <a href="https://health.utah.gov" target="_blank" rel="noopener noreferrer">
                Utah Health Department
              </a>
            </li>
          </ul>
        </FooterSection>
        
        <FooterSection>
          <h3>More Information</h3>
          <ul>
            <li>
              <Link to="/medical">
                📋 Medical Details
              </Link>
            </li>
            <li>
              <Link to="/resources">
                🔗 Resources & Links
              </Link>
            </li>
          </ul>
          <p style={{marginTop: '1rem', fontSize: '0.9rem'}}>
            Additional information about the medical journey and helpful resources.
          </p>
        </FooterSection>
        
        <FooterSection>
          <h3>Recognition</h3>
          <p>
            <strong>Fire Department "Life Saving" Award</strong><br/>
            Recognizing Eliza Soward for her life-saving CPR actions on November 28, 2024.
          </p>
          <ul style={{marginTop: '1rem'}}>
            <li>
              <Link
                to="/media-recognition"
                onClick={scrollToTop}
              >
                📰 Professional Media Coverage
              </Link>
            </li>
          </ul>
          <p style={{marginTop: '1rem', fontSize: '0.9rem'}}>
            This website serves as a tribute to all who participated in saving Maggie's life
            and supporting the Soward family during this miraculous week.
          </p>
        </FooterSection>
      </FooterContent>
      
      <Copyright>
        <p>
          © 2025 Soward Family • <strong>butdidyoudie.life</strong> • Made with <HeartIcon>❤️</HeartIcon> to honor Maggie's miracle 
          and thank our amazing community.
        </p>
        <p>
          <em>"Faith, Family, and Community - The miracle of November 28, 2024"</em>
        </p>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
